import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, FileText, CheckCircle, Clock, AlertCircle,
  DollarSign, Pen, Shield, ChevronRight, Sparkles, Leaf, Waves, Wind, Wrench,
  Play, CreditCard, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  cat_diarista: Sparkles,
  cat_jardinagem: Leaf,
  cat_piscina: Waves,
  cat_arcondicionado: Wind,
  cat_reforma: Wrench
};

const STATUS_CONFIG = {
  pending: { label: "Aguardando Assinatura", color: "bg-amber-100 text-amber-700", icon: Clock },
  active: { label: "Pronto para Início", color: "bg-blue-100 text-blue-700", icon: Play },
  in_execution: { label: "Em Execução", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  pending_approval: { label: "Aguardando Aprovação", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  completed: { label: "Concluído", color: "bg-slate-100 text-slate-700", icon: CheckCircle },
  disputed: { label: "Em Disputa", color: "bg-red-100 text-red-700", icon: AlertCircle },
  cancelled: { label: "Cancelado", color: "bg-slate-100 text-slate-500", icon: AlertCircle }
};

export default function ContractsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contracts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setContracts(data);
        }
      } catch (error) {
        console.error("Error fetching contracts:", error);
        toast.error("Erro ao carregar contratos");
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [token]);

  const [escrows, setEscrows] = useState({});

  useEffect(() => {
    const fetchEscrows = async () => {
      try {
        const response = await fetch(`${API_URL}/api/escrows`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const escrowMap = {};
          data.forEach(e => {
            escrowMap[e.contract_id] = e;
          });
          setEscrows(escrowMap);
        }
      } catch (error) {
        console.error("Error fetching escrows:", error);
      }
    };
    if (contracts.length > 0) {
      fetchEscrows();
    }
  }, [contracts, token]);

  const signContract = async (contractId) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/sign`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const updated = await response.json();
        setContracts(prev => prev.map(c => c.contract_id === contractId ? updated : c));
        toast.success("Contrato assinado com sucesso!");
      } else {
        throw new Error("Erro ao assinar contrato");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="contracts-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(user?.role === "provider" ? "/provider" : "/client")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              </Link>
              <span className="font-semibold text-slate-900">Contratos</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Meus Contratos</h1>
          <p className="text-slate-600">{contracts.length} contrato{contracts.length !== 1 ? "s" : ""}</p>
        </div>

        {contracts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum contrato</h3>
              <p className="text-slate-500 mb-4">
                Seus contratos aparecerão aqui após fechar um acordo.
              </p>
              <Button onClick={() => navigate("/budgets")}>Ver Orçamentos</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contracts.map((contract) => {
              const Icon = CATEGORY_ICONS[contract.category_id] || FileText;
              const status = STATUS_CONFIG[contract.status] || STATUS_CONFIG.pending;
              const StatusIcon = status.icon;
              const needsSignature = contract.status === "pending" && (
                (user?.role === "client" && !contract.client_signed) ||
                (user?.role === "provider" && !contract.provider_signed)
              );

              return (
                <Card key={contract.contract_id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                      {/* Contract Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-7 h-7 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900 text-lg">
                              Contrato #{contract.contract_id.slice(-8)}
                            </h3>
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mb-3">
                            Criado em {new Date(contract.created_at).toLocaleDateString("pt-BR")}
                          </p>
                          
                          {/* Scope Preview */}
                          {contract.scope?.description && (
                            <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                              {contract.scope.description}
                            </p>
                          )}

                          {/* Signatures Status */}
                          <div className="flex items-center gap-4 text-sm">
                            <div className={`flex items-center gap-1 ${contract.client_signed ? "text-emerald-600" : "text-slate-400"}`}>
                              {contract.client_signed ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                              <span>Cliente {contract.client_signed ? "assinou" : "pendente"}</span>
                            </div>
                            <div className={`flex items-center gap-1 ${contract.provider_signed ? "text-emerald-600" : "text-slate-400"}`}>
                              {contract.provider_signed ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                              <span>Prestador {contract.provider_signed ? "assinou" : "pendente"}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Value and Actions */}
                      <div className="lg:text-right">
                        <p className="text-sm text-slate-500">Valor Total</p>
                        <p className="text-2xl font-bold text-slate-900 mb-1">
                          R$ {contract.total_value?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </p>
                        
                        {contract.signal_amount > 0 && (
                          <p className="text-xs text-slate-500 mb-3">
                            Sinal: R$ {contract.signal_amount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                          </p>
                        )}

                        {contract.retention_percentage > 0 && (
                          <div className="flex items-center gap-1 text-xs text-amber-600 justify-end mb-3">
                            <Shield className="w-3 h-3" />
                            <span>Retenção: {(contract.retention_percentage * 100).toFixed(0)}% por {contract.retention_days} dias</span>
                          </div>
                        )}

                        {needsSignature && (
                          <Button 
                            className="bg-emerald-600 hover:bg-emerald-700 w-full lg:w-auto"
                            onClick={() => signContract(contract.contract_id)}
                            data-testid={`sign-contract-${contract.contract_id}`}
                          >
                            <Pen className="w-4 h-4 mr-2" />
                            Assinar Contrato
                          </Button>
                        )}

                        {/* Action buttons based on status */}
                        {contract.status === "active" && user?.role === "client" && escrows[contract.contract_id] && (
                          <div className="flex flex-col gap-2 mt-3">
                            <Button 
                              className="bg-emerald-600 hover:bg-emerald-700 w-full lg:w-auto"
                              onClick={() => navigate(`/payment/${escrows[contract.contract_id].escrow_id}`)}
                              data-testid={`pay-contract-${contract.contract_id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              Fazer Pagamento
                            </Button>
                          </div>
                        )}

                        {["active", "in_execution", "pending_approval"].includes(contract.status) && (
                          <Button 
                            variant="outline" 
                            className="w-full lg:w-auto mt-2"
                            onClick={() => navigate(`/execution/${contract.contract_id}`)}
                            data-testid={`view-execution-${contract.contract_id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            {user?.role === "provider" ? "Atualizar Execução" : "Acompanhar Serviço"}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Milestones Preview */}
                    {contract.milestones?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          Etapas ({contract.milestones.length})
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {contract.milestones.map((milestone, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs whitespace-nowrap">
                              {milestone.name || `Etapa ${idx + 1}`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Trust Info */}
        <Card className="mt-8 bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Sistema de Escrow eZelo.</h3>
                <p className="text-slate-300 text-sm">
                  Após ambas as partes assinarem, o pagamento será depositado em escrow. 
                  Os fundos são liberados apenas após a aprovação do serviço pelo cliente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
